import {
  API_BASE_URL,
  AUTH_STORAGE_KEY,
  REFRESH_TOKEN_STORAGE_KEY,
} from "config/envConfig";
import { handleUnauthorizedSession } from "lib/sessionExpired";

export class ApiError extends Error {
  constructor(message, { status, data } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

export function isSuccessResponse(resp) {
  if (!resp || typeof resp !== "object") return true;
  if (typeof resp.status === "boolean") return resp.status;
  if (typeof resp.success === "boolean") return resp.success;
  if (typeof resp.ok === "boolean") return resp.ok;
  return true;
}

export function extractAuthPayload(resp) {
  const root = resp?.data ?? resp ?? {};
  return {
    user: root.user ?? null,
    token: root.token ?? root.accessToken ?? null,
    refreshToken: root.refreshToken ?? null,
  };
}

const safeLocalStorage = () =>
  typeof window === "undefined" ? null : window.localStorage;

const readKey = (key) => {
  const ls = safeLocalStorage();
  return ls ? ls.getItem(key) : null;
};

const writeKey = (key, value) => {
  const ls = safeLocalStorage();
  if (!ls) return;
  if (value) ls.setItem(key, value);
  else ls.removeItem(key);
};

export function getAuthToken() {
  return readKey(AUTH_STORAGE_KEY);
}

export function setAuthToken(token) {
  writeKey(AUTH_STORAGE_KEY, token);
}

export function clearAuthToken() {
  setAuthToken(null);
}

export function getRefreshToken() {
  return readKey(REFRESH_TOKEN_STORAGE_KEY);
}

export function setRefreshToken(token) {
  writeKey(REFRESH_TOKEN_STORAGE_KEY, token);
}

export function clearRefreshToken() {
  setRefreshToken(null);
}

export function clearAuthTokens() {
  clearAuthToken();
  clearRefreshToken();
}

function buildHeaders({ headers, body, auth }) {
  const finalHeaders = new Headers(headers || {});
  const isFormData =
    typeof FormData !== "undefined" && body instanceof FormData;

  if (body !== undefined && !isFormData && !finalHeaders.has("Content-Type")) {
    finalHeaders.set("Content-Type", "application/json");
  }

  if (auth !== false) {
    const token = getAuthToken();
    if (token && !finalHeaders.has("Authorization")) {
      finalHeaders.set("Authorization", `Bearer ${token}`);
    }
  }

  return finalHeaders;
}

async function parseResponse(response) {
  if (response.status === 204) return null;
  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) return response.json();
  return response.text();
}

/**
 * Fetch wrapper — 2xx resolves with parsed body; otherwise throws `ApiError`.
 * @param {string} path - Relative path or absolute URL.
 * @param {RequestInit & { auth?: boolean }} options
 */
export async function apiFetch(path, options = {}) {
  const { method = "GET", body, headers, auth, signal, ...rest } = options;
  const url = /^https?:\/\//i.test(path) ? path : `${API_BASE_URL}${path}`;

  const init = {
    method,
    headers: buildHeaders({ headers, body, auth }),
    signal,
    ...rest,
  };

  if (body !== undefined) {
    const isFormData =
      typeof FormData !== "undefined" && body instanceof FormData;
    init.body =
      isFormData || typeof body === "string" ? body : JSON.stringify(body);
  }

  const response = await fetch(url, init);
  const data = await parseResponse(response);

  if (!response.ok) {
    if (response.status === 401 && auth !== false) {
      await handleUnauthorizedSession();
    }
    const message =
      (data && typeof data === "object" && (data.message || data.error)) ||
      response.statusText ||
      `Request failed with status ${response.status}`;
    throw new ApiError(message, { status: response.status, data });
  }

  return data;
}

export const api = {
  get: (path, options) => apiFetch(path, { ...options, method: "GET" }),
  post: (path, body, options) =>
    apiFetch(path, { ...options, method: "POST", body }),
  put: (path, body, options) =>
    apiFetch(path, { ...options, method: "PUT", body }),
  patch: (path, body, options) =>
    apiFetch(path, { ...options, method: "PATCH", body }),
  delete: (path, options) => apiFetch(path, { ...options, method: "DELETE" }),
};

export default api;
